import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import i18n from '@/i18n';
import {
  translationsApi,
  type ImportRequest,
  type TranslationCreateInput,
  type TranslationListParams,
  type TranslationUpdateInput,
} from '../api/translations.api';
import { translationsKeys } from '../api/translations.keys';

export function useTranslationsList(params: TranslationListParams) {
  return useQuery({
    queryKey: translationsKeys.list(params),
    queryFn: () => translationsApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useSupportedLocales() {
  return useQuery({
    queryKey: translationsKeys.locales(),
    queryFn: () => translationsApi.locales(),
    staleTime: 5 * 60_000,
  });
}

export function useTranslationCoverage(enabled = true) {
  return useQuery({
    queryKey: translationsKeys.coverage(),
    queryFn: () => translationsApi.coverage(),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TranslationCreateInput) => translationsApi.create(input),
    onSuccess: () => {
      toast.success(i18n.t('translations.toast.created'));
      qc.invalidateQueries({ queryKey: translationsKeys.lists() });
      qc.invalidateQueries({ queryKey: translationsKeys.coverage() });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail ?? i18n.t('translations.toast.createFailed'));
    },
  });
}

export function useUpdateTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, input }: { key: string; input: TranslationUpdateInput }) =>
      translationsApi.update(key, input),
    onSuccess: () => {
      toast.success(i18n.t('translations.toast.saved'));
      qc.invalidateQueries({ queryKey: translationsKeys.lists() });
      qc.invalidateQueries({ queryKey: translationsKeys.coverage() });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail ?? i18n.t('translations.toast.saveFailed'));
    },
  });
}

export function useDeleteTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => translationsApi.remove(key),
    onSuccess: () => {
      toast.success(i18n.t('translations.toast.deleted'));
      qc.invalidateQueries({ queryKey: translationsKeys.lists() });
      qc.invalidateQueries({ queryKey: translationsKeys.coverage() });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail ?? i18n.t('translations.toast.deleteFailed'));
    },
  });
}

export function useImportTranslations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ImportRequest) => translationsApi.import(request),
    onSuccess: (result) => {
      toast.success(
        i18n.t('translations.toast.imported', {
          created: result.keysCreated,
          inserted: result.valuesInserted,
          updated: result.valuesUpdated,
        }),
      );
      qc.invalidateQueries({ queryKey: translationsKeys.lists() });
      qc.invalidateQueries({ queryKey: translationsKeys.coverage() });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail ?? i18n.t('translations.toast.importFailed'));
    },
  });
}
